import {
  DamageDetectionItemDto,
  VehicleDamageDataDto,
  VehicleDamageDto,
} from "@/core/api/generated/v1";

import { DamageDetectionItemValues } from "../../components/Entity/DamageDetection/DamageDetectionCreateUpdate";
import { ArrayHelper } from "../../helpers/array";
import { VisualModelHelper } from "../../helpers/entity/vehicleVisualModel";
import { IdHelper } from "../../helpers/id";
import { StringHelper } from "../../helpers/string";
import { SvgHelper } from "../../helpers/svg";
import { SvgPoint } from "../../ts/svg";
import { GenericDamage, VisualModelPoint, VisualModelPointPartial } from "../../ts/visualModel";
import { VehicleDamageDataInputDtoLocal } from "./vehicleDamage";

export class VehicleVisualModelMapper {
  // #region SvgPoint

  public static mapVehicleDamageDtoToSvgPoint(
    source: VehicleDamageDto,
    dest?: Partial<SvgPoint>,
  ): SvgPoint | null {
    const coords =
      dest?.coords || SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoordsOrNull(source.point);
    if (!coords) {
      return null;
    }
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newId(),
      coords: coords,
      style: {
        ...VisualModelHelper.getSvgPointStyleForVehicleDamage(source),
        ...dest?.style,
      },
      title:
        dest?.title ||
        StringHelper.join([source.partType?.name, source.damageType?.name], " - ", {
          skipEmpty: true,
        }),
      data: dest?.data || source,
    };
  }

  public static mapVehicleDamageDataDtoToSvgPoint(
    source: VehicleDamageDataDto,
    dest?: Partial<SvgPoint>,
  ): SvgPoint | null {
    const coords =
      dest?.coords || SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoordsOrNull(source.point);
    if (!coords) {
      return null;
    }
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newId(),
      coords: coords,
      style: {
        ...VisualModelHelper.getSvgPointStyleForVehicleDamage(source),
        ...dest?.style,
      },
      title:
        dest?.title ||
        StringHelper.join([source.partType?.name, source.damageType?.name], " - ", {
          skipEmpty: true,
        }),
      data: dest?.data || source,
    };
  }

  public static mapDamageDetectionItemDtoToSvgPoint(
    source: DamageDetectionItemDto,
    dest?: Partial<SvgPoint>,
  ): SvgPoint | null {
    const coords =
      dest?.coords || SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoordsOrNull(source.point);
    if (!coords) {
      return null;
    }
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newId(),
      coords: coords,
      style: {
        ...VisualModelHelper.defaultSvgPointStyleForDamage,
        ...dest?.style,
      },
      title:
        dest?.title ||
        StringHelper.join([source.partType?.name, source.damageType?.name], " - ", {
          skipEmpty: true,
        }),
      data: dest?.data || source,
    };
  }

  public static mapDamageDetectionItemValuesToSvgPoint(
    source: DamageDetectionItemValues,
    dest?: Partial<SvgPoint>,
  ): SvgPoint<DamageDetectionItemValues> | null {
    if (!source.point) {
      return null;
    }
    return {
      ...dest,
      id: source.id || IdHelper.newId(),
      coords: SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoords(source.point),
      style: {
        ...VisualModelHelper.defaultSvgPointStyleForDamage,
        ...dest?.style,
      },
      title:
        dest?.title ||
        StringHelper.join(
          [
            source.partTypeLocal?.name || source.partType?.name,
            source.damageTypeLocal?.name || source.damageType?.name,
          ],
          " - ",
          { skipEmpty: true },
        ),
      data: source,
    };
  }

  public static mapGenericDamageToSvgPoint<TDamage extends GenericDamage>(
    source: TDamage,
    dest?: Partial<SvgPoint>,
  ): SvgPoint<TDamage> | null {
    if (!source.point) {
      return null;
    }
    return {
      ...dest,
      id: source.id || IdHelper.newId(),
      coords: SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoords(source.point),
      style: {
        ...VisualModelHelper.defaultSvgPointStyleForDamage,
        ...dest?.style,
        isAttentionRequired: dest?.style?.isAttentionRequired || source.isRepositioningRequired,
      },
      title:
        dest?.title ||
        (source.isRepositioningRequired
          ? "Please reposition to the correct location."
          : undefined) ||
        StringHelper.join(
          [
            source.partType?.name || source.partTypeRef?.name,
            source.damageType?.name || source.damageTypeRef?.name,
          ],
          " - ",
          { skipEmpty: true },
        ),
      data: source,
    };
  }

  // #endregion SvgPoint

  // #region VisualModelPoint

  public static mapVehicleDamageDtoToVisualModelPoint(
    source: VehicleDamageDto,
    dest?: VisualModelPointPartial<VehicleDamageDto>,
  ): VisualModelPoint<VehicleDamageDto> | null {
    const svgPoint = this.mapVehicleDamageDtoToSvgPoint(source, dest);
    if (!svgPoint) {
      return null;
    }
    return {
      ...svgPoint,
      data: source,
      meta: {
        area: source.area,
        projection: source.projection,
        ...dest?.meta,
      },
    };
  }

  public static mapVehicleDamageDataDtoToVisualModelPoint(
    source: VehicleDamageDataDto,
    dest?: VisualModelPointPartial<VehicleDamageDataDto>,
  ): VisualModelPoint<VehicleDamageDataDto> | null {
    const svgPoint = this.mapVehicleDamageDataDtoToSvgPoint(source, dest);
    if (!svgPoint) {
      return null;
    }
    return {
      ...svgPoint,
      data: source,
      meta: {
        area: source.area,
        projection: source.projection,
        ...dest?.meta,
      },
    };
  }

  public static mapDamageDetectionItemDtoToVisualModelPoint(
    source: DamageDetectionItemDto,
    dest?: VisualModelPointPartial<DamageDetectionItemDto>,
  ): VisualModelPoint<DamageDetectionItemDto> | null {
    const svgPoint = this.mapDamageDetectionItemDtoToSvgPoint(source, dest);
    if (!svgPoint) {
      return null;
    }
    return {
      ...svgPoint,
      data: dest?.data || source,
      meta: {
        area: source.area,
        projection: source.projection,
        ...dest?.meta,
      },
    };
  }

  public static mapDamageDetectionItemValuesToVisualModelPoint(
    source: DamageDetectionItemValues,
    dest?: VisualModelPointPartial<DamageDetectionItemValues>,
  ): VisualModelPoint<DamageDetectionItemValues> | null {
    const svgPoint = this.mapDamageDetectionItemValuesToSvgPoint(source, dest);
    if (!svgPoint) {
      return null;
    }
    return {
      ...svgPoint,
      data: source,
      meta: {
        area: source.area,
        projection: source.projection,
        ...dest?.meta,
      },
    };
  }

  public static mapGenericDamageToVisualModelPoint<TDamage extends GenericDamage>(
    source: TDamage,
    dest?: VisualModelPointPartial<TDamage>,
  ): VisualModelPoint<TDamage> | null {
    const svgPoint = this.mapGenericDamageToSvgPoint(source, dest);
    if (!svgPoint) {
      return null;
    }
    return {
      ...svgPoint,
      data: source,
      meta: {
        area: source.area,
        projection: source.projection,
        ...dest?.meta,
      },
    };
  }

  // #endregion VisualModelPoint

  // #region GenericDamage

  public static mapVehicleDamageDtoToGenericDamage(
    source: VehicleDamageDto,
    dest?: Partial<GenericDamage<VehicleDamageDto>>,
  ): GenericDamage<VehicleDamageDto> {
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newTempFeId(),
      visualModelId: dest?.visualModelId || source.visualModel?.id,
      vehicleDamageId: source.id,
      vehicleDamage: source,
      partTypeId: source.partType?.id,
      partType: null,
      partTypeRef: source.partType,
      damageTypeId: source.damageType?.id,
      damageType: null,
      damageTypeRef: source.damageType,
      area: source.area,
      projection: source.projection,
      point: source.point,
      notes: source.notes,
      attachments: source.attachments,
      svgPathMetadata: null,
      data: source,
    };
  }

  public static mapVehicleDamageDataDtoToGenericDamage(
    source: VehicleDamageDataDto,
    dest?: Partial<GenericDamage<VehicleDamageDataDto>>,
  ): GenericDamage<VehicleDamageDataDto> {
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newTempFeId(),
      visualModelId: dest?.visualModelId || source.visualModelId,
      vehicleDamageId: IdHelper.nullIfTempFeId(source.id),
      vehicleDamage: null,
      partTypeId: source.partType?.id,
      partType: null,
      partTypeRef: source.partType,
      damageTypeId: source.damageType?.id,
      damageType: null,
      damageTypeRef: source.damageType,
      area: source.area,
      projection: source.projection,
      point: source.point,
      notes: source.notes,
      attachments: source.attachments,
      svgPathMetadata: null,
      data: source,
    };
  }

  public static mapVehicleDamageDataInputDtoToGenericDamage(
    source: VehicleDamageDataInputDtoLocal,
    dest?: Partial<GenericDamage<VehicleDamageDataInputDtoLocal>>,
  ): GenericDamage<VehicleDamageDataInputDtoLocal> {
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newTempFeId(),
      originalId: dest?.originalId || source.originalId || undefined,
      originalIds:
        ArrayHelper.nullIfEmpty(dest?.originalIds) ||
        ArrayHelper.nullIfEmpty(source.originalIds) ||
        undefined,
      visualModelId: dest?.visualModelId || source.visualModelId,
      vehicleDamageId: IdHelper.nullIfTempFeId(source.id),
      vehicleDamage: null,
      partTypeId: source.partTypeId,
      partType: source.partTypeLocal,
      partTypeRef: source.partType,
      damageTypeId: source.damageTypeId,
      damageType: source.damageTypeLocal,
      damageTypeRef: source.damageType,
      area: source.area,
      projection: source.projection,
      point: source.point,
      notes: source.notes,
      attachments: source.attachments,
      svgPathMetadata: null,
      data: source,
    };
  }

  public static mapDamageDetectionItemToGenericDamage(
    source: DamageDetectionItemDto,
    extra: { visualModelId: Nil<string> },
    dest?: Partial<GenericDamage<DamageDetectionItemDto>>,
  ): GenericDamage<DamageDetectionItemDto> {
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newTempFeId(),
      visualModelId: dest?.visualModelId || extra.visualModelId,
      vehicleDamageId: source.damage?.id,
      vehicleDamage: null,
      partTypeId: source.partType?.id,
      partType: null,
      partTypeRef: source.partType,
      damageTypeId: source.damageType?.id,
      damageType: null,
      damageTypeRef: source.damageType,
      area: source.area,
      projection: source.projection,
      point: source.point,
      notes: source.notes,
      attachments: source.attachments,
      svgPathMetadata: null,
      data: source,
    };
  }

  public static mapDamageDetectionItemValuesToGenericDamage(
    source: DamageDetectionItemValues,
    extra: { visualModelId: Nil<string> },
    dest?: Partial<GenericDamage<DamageDetectionItemValues>>,
  ): GenericDamage<DamageDetectionItemValues> {
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newTempFeId(),
      visualModelId: dest?.visualModelId || extra.visualModelId,
      vehicleDamageId: source.damage?.id,
      vehicleDamage: null,
      partTypeId: source.partTypeId || source.partTypeLocal?.id || source.partType?.id,
      partType: source.partTypeLocal,
      partTypeRef: source.partType,
      damageTypeId: source.damageTypeId || source.damageTypeLocal?.id || source.damageType?.id,
      damageType: source.damageTypeLocal,
      damageTypeRef: source.damageType,
      area: source.area,
      projection: source.projection,
      point: source.point,
      notes: source.notes,
      attachments: source.attachments,
      svgPathMetadata: source.svgPathMetadata,
      data: source,
    };
  }

  public static mapGenericDamageToVehicleDamageDataInputDto(
    source: GenericDamage<VehicleDamageDataInputDtoLocal>,
    dest?: Partial<VehicleDamageDataInputDtoLocal>,
  ): VehicleDamageDataInputDtoLocal {
    return {
      ...source.data,
      ...dest,
      id: dest?.id || source.id || source.data?.id || IdHelper.newTempFeId(),
      idBeforeUpdateLocal: dest?.idBeforeUpdateLocal || source.data?.idBeforeUpdateLocal,
      originalId: dest?.originalId || source?.originalId,
      originalIds:
        ArrayHelper.nullIfEmpty(dest?.originalIds) || ArrayHelper.nullIfEmpty(source?.originalIds),
      visualModelId: dest?.visualModelId || source.visualModelId || undefined,
      partTypeId: dest?.partTypeId || source.partTypeId || undefined,
      partType: dest?.partType || source.partTypeRef,
      partTypeLocal: dest?.partTypeLocal || source.partType,
      damageTypeId: dest?.damageTypeId || source.damageTypeId || undefined,
      damageType: dest?.damageType || source.damageTypeRef,
      damageTypeLocal: dest?.damageTypeLocal || source.damageType,
      area: dest?.area || source.area,
      projection: dest?.projection || source.projection,
      point: dest?.point || source.point,
      notes: dest?.notes || source.notes,
      attachments: dest?.attachments || source.attachments || undefined,
    };
  }

  public static mapGenericDamageToDamageDetectionItemValues(
    source: GenericDamage<DamageDetectionItemValues>,
    dest?: Partial<DamageDetectionItemValues>,
  ): DamageDetectionItemValues {
    return {
      ...dest,
      id: dest?.id || source.id || IdHelper.newTempFeId(),
      partTypeId: source.partType?.id,
      partType: undefined,
      partTypeLocal: source.partType,
      damageTypeId: source.damageType?.id,
      damageType: undefined,
      damageTypeLocal: source.damageType,
      area: source.area,
      projection: source.projection,
      point: source.point,
      notes: source.notes,
      attachments: source.attachments || undefined,
      svgPathMetadata: source.svgPathMetadata,
    };
  }

  // #endregion GenericDamage
}
